import { useStrategySimulation } from '../../hooks/useStrategySimulation';
import { TemplatesPanel } from './TemplatesPanel';
import { PillarIcon } from './PillarIcon';
import { PILLAR_COLORS } from '../../constants/pillarColors';
import { EconomyTab } from './EconomyTab';
import { MilitaryTab } from './MilitaryTab';
import { ScienceTab } from './ScienceTab';
import { OutcomesPanel } from './OutcomesPanel';
import { COLORS } from '../../constants/theme';
import type { StrategyShellProps } from '../../pages/Strategy';
import type { DesktopStrategyTabKey } from '../../types/strategy';

const TABS: { key: DesktopStrategyTabKey; label: string }[] = [
  { key: 'economy', label: 'Buildings' },
  { key: 'military', label: 'Military' },
  { key: 'science', label: 'Sciences' },
];

/**
 * SF-172: thin wrapper around today's desktop Strategy Planner JSX, moved here verbatim — tab bar
 * + TemplatesPanel row, two-column `grid-cols-1 lg:grid-cols-[1fr_320px]` grid with sticky
 * OutcomesPanel. Keeps the exact `sr-only` `<h1>` and zero other visual/behavioral change from
 * today's rendering. Owns its own `useStrategySimulation()` call — `projected` no longer travels
 * through `StrategyShellProps`; each shell calls the hook independently (SF-173: mobile now does
 * the same for its own Outcome tab). Tab strip gains full WAI-ARIA (new-file hygiene, per the
 * SF-160/161 precedent — new shells get full ARIA even when their JSX is otherwise moved verbatim).
 */
export function DesktopStrategyShell({
  province,
  formState,
  dispatch,
  desktopActiveTab,
  setDesktopActiveTab,
  totalLand,
  population,
  race,
  existingBooks,
  templates,
  activeStrategyRef,
  templatesLoading,
  currentTemplateId,
  isDirty,
  handleLoadTemplate,
  handleActivateCustom,
  handleActivatePremade,
  handleDeactivate,
  handleSaveNew,
  handleSaveChanges,
  handleRename,
  handleDelete,
}: StrategyShellProps) {
  const projected = useStrategySimulation(formState, province);

  return (
    <div>
      <h1 className="sr-only">Strategy Planner</h1>

      {/* Tab Bar + Template selector — single row */}
      <div className="flex items-end justify-between border-b mb-4" style={{ borderColor: COLORS.border }}>
        <div className="flex" role="tablist">
          {TABS.map((t) => {
            const isActive = desktopActiveTab === t.key;
            const pillarColor = t.key in PILLAR_COLORS
              ? PILLAR_COLORS[t.key as keyof typeof PILLAR_COLORS].primary
              : COLORS.text.secondary;
            return (
              <button
                key={t.key}
                id={`strategy-tab-${t.key}`}
                role="tab"
                aria-selected={isActive}
                aria-controls={`strategy-panel-${t.key}`}
                onClick={() => setDesktopActiveTab(t.key)}
                className="px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5"
                style={{
                  borderBottomColor: isActive ? pillarColor : 'transparent',
                  color: isActive ? pillarColor : COLORS.text.tertiary,
                }}
              >
                {t.key in PILLAR_COLORS && <PillarIcon pillar={t.key as keyof typeof PILLAR_COLORS} size={14} />}
                {t.label}
              </button>
            );
          })}
        </div>
        <div className="pb-2">
          <TemplatesPanel
            templates={templates}
            activeStrategyRef={activeStrategyRef}
            onLoadTemplate={handleLoadTemplate}
            onActivateCustom={handleActivateCustom}
            onActivatePremade={handleActivatePremade}
            onDeactivate={handleDeactivate}
            onSaveNew={handleSaveNew}
            onSaveChanges={handleSaveChanges}
            onRename={handleRename}
            onDelete={handleDelete}
            isDirty={isDirty}
            currentTemplateId={currentTemplateId}
            loading={templatesLoading}
          />
        </div>
      </div>

      {/* Two-column layout: tab content + outcomes panel */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* Left: Tab Content */}
        <div
          className="rounded-lg p-4"
          style={{
            backgroundColor: COLORS.card,
            border: `1px solid ${COLORS.border}`,
          }}
        >
          {desktopActiveTab === 'economy' && (
            <div role="tabpanel" id="strategy-panel-economy" aria-labelledby="strategy-tab-economy">
              <EconomyTab state={formState} dispatch={dispatch} totalLand={totalLand} />
            </div>
          )}
          {desktopActiveTab === 'military' && (
            <div role="tabpanel" id="strategy-panel-military" aria-labelledby="strategy-tab-military">
              <MilitaryTab
                state={formState}
                dispatch={dispatch}
                totalLand={totalLand}
                population={population}
                race={race}
              />
            </div>
          )}
          {desktopActiveTab === 'science' && (
            <div role="tabpanel" id="strategy-panel-science" aria-labelledby="strategy-tab-science">
              <ScienceTab
                state={formState}
                dispatch={dispatch}
                totalLand={totalLand}
                existingBooks={existingBooks}
              />
            </div>
          )}
        </div>

        {/* Right: Outcomes Panel (sticky) */}
        <div className="lg:sticky lg:top-4 lg:self-start">
          <OutcomesPanel projected={projected} />
        </div>
      </div>
    </div>
  );
}
